import { useEffect, useRef, useState } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { fetchCurrentUserDetailsIfNeeded } from '@/actions/userActions'
import { MEMBERSHIP_STATUS } from '@/reducers/auth/userReducer'
import { selectPrimaryMembership } from '@/selectors/user.selectors'
import { selectCatchUpBookkeepingIsUnpaid } from '@/components/CatchUpBookkeepingStatus/catchUpBookkeepingStatus.selector'
import { useReselector } from '@/utils/sharedHooks'
import { useAppDispatch } from '@/utils/typeHelpers'
import { useCardSummary } from '@/features/billing/payment-method/useCardSummary'
import { usePaymentMethodUpdate } from '@/features/billing/payment-method/usePaymentMethodUpdate'
import { PaymentMethodForm } from '@/features/billing/payment-method/PaymentMethodForm'
import { savedCopy } from '@/features/billing/payment-method/paymentMethodCopy'
import type { CardSummary } from '@/features/billing/payment-method/payment-method.types'
import { CatchUpBookkeepingUnpaid } from './CatchUpBookkeepingUnpaid'
import { Button, Card, Link, Text } from '@/ui'
import { TypographyStyles } from '@/styles'

const StatusHeader: Partial<Record<MEMBERSHIP_STATUS, string>> = {
  paid: 'Restored',
  pending: 'Pending',
  unpaid: 'Suspended',
}

const AccountSuspended = () => {
  const dispatch = useAppDispatch()
  const loadCardSummary = useCardSummary()
  const navigate = useNavigate()
  const membership = useReselector(selectPrimaryMembership)
  const currentMembershipStatus = membership?.status
  const catchUpBookkeepingIsUnpaid = useReselector(
    selectCatchUpBookkeepingIsUnpaid
  )
  const [currentCard, setCurrentCard] = useState<CardSummary | null>(null)
  const [savedMessage, setSavedMessage] = useState('')
  const paymentMethodUpdate = usePaymentMethodUpdate({
    surface: 'suspended page',
    onSaved: (card) => {
      setSavedMessage(savedCopy(card))
      dispatch(fetchCurrentUserDetailsIfNeeded(true))
    },
  })
  const formOpen =
    paymentMethodUpdate.status !== 'idle' &&
    paymentMethodUpdate.status !== 'saved'
  const payButtonRef = useRef<HTMLButtonElement>(null)
  const wasFormOpenRef = useRef(false)

  useEffect(() => {
    // Focus falls to <body> when the form unmounts unless something claims it, so send it
    // back to the button that reopens the form.
    if (wasFormOpenRef.current && !formOpen) payButtonRef.current?.focus()
    wasFormOpenRef.current = formOpen
  }, [formOpen])

  const header = StatusHeader[currentMembershipStatus ?? 'unpaid']

  const openInlineForm = (): void => {
    setSavedMessage('')
    void loadCardSummary().then(setCurrentCard)
    paymentMethodUpdate.open()
  }

  const unpaidAction = formOpen ? (
    <div className="mt-4 w-full max-w-[560px]">
      <PaymentMethodForm
        update={paymentMethodUpdate}
        flow="account"
        currentCard={currentCard}
        onCancel={paymentMethodUpdate.cancel}
        focusOnMount
      />
    </div>
  ) : (
    <Button ref={payButtonRef} onClick={openInlineForm}>
      Pay and Restore Account
    </Button>
  )

  if (catchUpBookkeepingIsUnpaid) {
    return <CatchUpBookkeepingUnpaid />
  }

  return currentMembershipStatus &&
    !(currentMembershipStatus in StatusHeader) ? (
    <Navigate replace to="/dashboard" />
  ) : (
    <Card
      className="sm:p-16"
      childrenClassName="flex flex-col items-center justify-center gap-4"
    >
      <Text variant={TypographyStyles.H1} className="text-center">
        Account {header}
      </Text>
      {currentMembershipStatus === 'unpaid' && (
        <>
          <Text variant={TypographyStyles.BODY_SM} className="text-center">
            Your last payment for Heard has failed.
          </Text>
          <Text variant={TypographyStyles.BODY_SM} className="text-center">
            You&apos;re unable to use Heard until you update with a valid form
            of payment. Don&apos;t worry, all your data is safe and secure.
          </Text>
        </>
      )}
      {currentMembershipStatus === 'pending' && (
        <Text variant={TypographyStyles.BODY_SM} className="text-center">
          Thank you for updating your payment method! Your account is currently
          pending and should be updated within 5 minutes. Please refresh the
          page to check if your account has been restored.
        </Text>
      )}
      {currentMembershipStatus === 'paid' && (
        <Text variant={TypographyStyles.BODY_SM} className="text-center">
          Your account has been restored! Please click the button below to go
          back to your dashboard or any of the sidebar items to get back to
          where you were!
        </Text>
      )}
      {currentMembershipStatus === MEMBERSHIP_STATUS.unpaid && unpaidAction}
      <Text
        role="status"
        variant={TypographyStyles.BODY_SM}
        // sr-only while empty keeps the live region mounted without adding a flex gap.
        className={savedMessage ? 'text-center' : 'sr-only'}
      >
        {savedMessage}
      </Text>
      {currentMembershipStatus === MEMBERSHIP_STATUS.paid && (
        <Button onClick={() => navigate('/dashboard')}>
          Return to Dashboard
        </Button>
      )}
      {/* We have a selector for this in `user.selectors.ts`, but since we're already using a
            selector to get the user's `membership.status`, we can reduce using another selector
            call for this. */}
      {['pending', 'unpaid'].includes(currentMembershipStatus ?? '') && (
        <Text variant={TypographyStyles.BODY_SM} className="text-center">
          Is this a mistake? Contact us via email at{' '}
          <Link variant="secondary" href="mailto:contact@joinheard.com">
            contact@joinheard.com
          </Link>
          .
        </Text>
      )}
    </Card>
  )
}

export { AccountSuspended }
